import { useEffect, useId, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Form, Formik, FormikHelpers } from 'formik';
import { Autocomplete, Button, Collapse, Divider, IconButton, TextField, useMediaQuery, useTheme } from '@mui/material';
import ClearIcon from '@mui/icons-material/ClearTwoTone';
import SearchIcon from '@mui/icons-material/SearchTwoTone';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLessTwoTone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreTwoTone';
import dayjs from 'dayjs';
import { Customer } from '@api/Trinium/Endpoints/Customer/types';
import { getStringOrBlank } from '@/Infrastructure/Utilities/string.type';
import SubmitFormButton from '@forms/Components/SubmitFormButton';
import {
  getResearchFilterEmptyValue,
  isResearchFilterValueEmpty,
  persistFilterFieldNames,
  readPersistedFilterFieldNames,
  RESEARCH_FORM_FIELD_DEFINITIONS,
  RESEARCH_PINNED_FIELD_NAMES,
  ResearchFormFieldDefinition,
  ResearchFormFields,
  researchValidationSchema
} from './types';
import { renderFilterField } from './RenderFilterFields';

import styles from './ResearchGridToolbar.module.css';

/**
 * Research (Dispatch) Grid Search Form component
 */
export default function ResearchForm() {
  const [searchParams, setSearchParams] = useSearchParams();
  const formId = useId();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /** Start collapsed when arriving with an active search (results are already displayed) */
  const [isExpanded, setIsExpanded] = useState(() => searchParams.toString() === '');

  /** Controlled input text for the Add Filter dropdown so a selection doesn't linger and filter the option list */
  const [addFilterInputValue, setAddFilterInputValue] = useState('');

  /**
   * Initial Values for the Formik Form determined by the search parameters
   */
  const initialValues: ResearchFormFields = useMemo(() => {
    const startRange = searchParams.get('startRange') ? dayjs(searchParams.get('startRange')) : dayjs().add(-1, 'year');
    const endRange = searchParams.get('endRange') ? dayjs(searchParams.get('endRange')) : dayjs();
    const custId = searchParams.get('customerId');
    const custName = searchParams.get('customerName');
    const createdByUserCode = searchParams.get('createdByUserCode');
    const createdByUserName = searchParams.get('createdByUserName');
    const delvStartRange = searchParams.get('delvStartDate') ? dayjs(searchParams.get('delvStartDate')) : null;
    const delvEndRange = searchParams.get('delvEndDate') ? dayjs(searchParams.get('delvEndDate')) : null;
    const schdPuStartRange = searchParams.get('schdPuStartDate') ? dayjs(searchParams.get('schdPuStartDate')) : null;
    const schdPuEndRange = searchParams.get('schdPuEndDate') ? dayjs(searchParams.get('schdPuEndDate')) : null;
    const schdDelvStartRange = searchParams.get('schdDelvStartDate') ? dayjs(searchParams.get('schdDelvStartDate')) : null;
    const schdDelvEndRange = searchParams.get('schdDelvEndDate') ? dayjs(searchParams.get('schdDelvEndDate')) : null;
    const consignorId = searchParams.get('consignorId');
    const consignorName = searchParams.get('consignorName');
    const consigneeId = searchParams.get('consigneeId');
    const consigneeName = searchParams.get('consigneeName');

    return {
      jobDateRange: [startRange, endRange],
      order: getStringOrBlank(searchParams.get('order')),
      customer: custId ? ({ id: custId, name: custName } as Customer) : null,
      createdBy: createdByUserCode && createdByUserName ? `(${createdByUserCode}) ${createdByUserName}` : '',
      container: searchParams.get('container') ? decodeURIComponent(searchParams.get('container') || '').split(',') : null,
      masterBol: getStringOrBlank(searchParams.get('masterBol')),
      driver: getStringOrBlank(searchParams.get('driver')),
      customerRef: getStringOrBlank(searchParams.get('customerRef')),
      chassis: getStringOrBlank(searchParams.get('chassis')),
      bookingRef: getStringOrBlank(searchParams.get('bookingRef')),
      onlyApprovedCharges: searchParams.get('isPrimaryChgApproved')
        ? searchParams.get('isPrimaryChgApproved') === 'yes'
          ? 'Only Approved'
          : 'Only Unapproved'
        : 'All',
      delvDateRange: [delvStartRange, delvEndRange],
      schdPuDateRange: [schdPuStartRange, schdPuEndRange],
      schdDelvDateRange: [schdDelvStartRange, schdDelvEndRange],
      pickupLocation: consignorId ? { id: consignorId, name: consignorName ?? undefined } : null,
      deliveryLocation: consigneeId ? { id: consigneeId, name: consigneeName ?? undefined } : null
    };
  }, [searchParams]);

  const handleFormikSubmit = (values: ResearchFormFields, helpers: FormikHelpers<ResearchFormFields>) => {
    const {
      jobDateRange,
      delvDateRange,
      schdPuDateRange,
      schdDelvDateRange,
      customer,
      createdBy,
      onlyApprovedCharges,
      pickupLocation,
      deliveryLocation,
      ...formValues
    } = values;

    // Trim leading/trailing whitespace from all free-text fields
    const trimmedFormValues = Object.fromEntries(
      Object.entries(formValues).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
    ) as typeof formValues;

    // Reflect the trimmed values back into the form inputs
    void helpers.setValues({ ...values, ...trimmedFormValues });

    let manualProps: Record<string, string> = {
      startRange: jobDateRange[0].format('YYYY-MM-DD'),
      endRange: jobDateRange[1].format('YYYY-MM-DD')
    };

    if (delvDateRange[0]?.isValid()) {
      manualProps = {
        ...manualProps,
        delvStartDate: delvDateRange[0].format('YYYY-MM-DD')
      };
    }

    if (delvDateRange[1]?.isValid()) {
      manualProps = {
        ...manualProps,
        delvEndDate: delvDateRange[1].format('YYYY-MM-DD')
      };
    }

    if (schdPuDateRange[0]?.isValid()) {
      manualProps = {
        ...manualProps,
        schdPuStartDate: schdPuDateRange[0].format('YYYY-MM-DD')
      };
    }

    if (schdPuDateRange[1]?.isValid()) {
      manualProps = {
        ...manualProps,
        schdPuEndDate: schdPuDateRange[1].format('YYYY-MM-DD')
      };
    }

    if (schdDelvDateRange[0]?.isValid()) {
      manualProps = {
        ...manualProps,
        schdDelvStartDate: schdDelvDateRange[0].format('YYYY-MM-DD')
      };
    }

    if (schdDelvDateRange[1]?.isValid()) {
      manualProps = {
        ...manualProps,
        schdDelvEndDate: schdDelvDateRange[1].format('YYYY-MM-DD')
      };
    }

    if (customer) {
      manualProps = {
        ...manualProps,
        customerId: customer.id,
        customerName: customer.name
      };
    }

    if (createdBy) {
      const code = createdBy.split(')')[0].replace('(', '');
      const name = createdBy.split(')')[1].trim();
      manualProps = {
        ...manualProps,
        createdByUserCode: code,
        createdByUserName: name
      };
    }

    if (pickupLocation) {
      manualProps = {
        ...manualProps,
        consignorId: pickupLocation.id,
        ...(pickupLocation.name ? { consignorName: pickupLocation.name } : {})
      };
    }

    if (deliveryLocation) {
      manualProps = {
        ...manualProps,
        consigneeId: deliveryLocation.id,
        ...(deliveryLocation.name ? { consigneeName: deliveryLocation.name } : {})
      };
    }

    if (onlyApprovedCharges !== 'All') {
      manualProps = {
        ...manualProps,
        isPrimaryChgApproved: onlyApprovedCharges === 'Only Approved' ? 'yes' : 'no'
      };
    }

    const filteredValues: Record<string, string> = {
      ...manualProps,
      ...Object.fromEntries(
        Object.entries(trimmedFormValues)
          .filter(([, value]) => value)
          .map(([key, value]) => [key, String(value)])
      )
    };

    setSearchParams(filteredValues);
    // In mobile widths close the form after submitting since results are already visible and the form takes up most of the screen when expanded
    if (isMobile) {
      setIsExpanded(false);
    }
  };

  /** Non-pinned filter fields implied as active by the current URL search params (e.g. a shared/bookmarked link) */
  const impliedActiveFieldNames = useMemo(() => {
    return RESEARCH_FORM_FIELD_DEFINITIONS.filter(
      (definition) => !definition.pinned && !isResearchFilterValueEmpty(definition.dataType, initialValues[definition.fieldName])
    ).map((definition) => definition.fieldName);
  }, [initialValues]);

  /** Pinned fields always show up; persisted fields are whatever the user previously added via the "Add Filter" dropdown */
  const [activeFieldNames, setActiveFieldNames] = useState<ResearchFormFieldDefinition['fieldName'][]>(() =>
    Array.from(new Set([...RESEARCH_PINNED_FIELD_NAMES, ...readPersistedFilterFieldNames(), ...impliedActiveFieldNames]))
  );

  /** Add (never remove) fields implied by the URL search params - e.g. a colleague's shared link should show its filters */
  useEffect(() => {
    if (impliedActiveFieldNames.length === 0) return;

    setActiveFieldNames((current) => {
      const merged = Array.from(new Set([...current, ...impliedActiveFieldNames]));
      return merged.length === current.length ? current : merged;
    });
  }, [impliedActiveFieldNames]);

  const availableFields = useMemo(
    () =>
      RESEARCH_FORM_FIELD_DEFINITIONS.filter((definition) => !definition.pinned && !activeFieldNames.includes(definition.fieldName)).sort(
        (a, b) => a.label.localeCompare(b.label)
      ),
    [activeFieldNames]
  );

  const handleAddField = (definition: ResearchFormFieldDefinition) => {
    setActiveFieldNames((current) => {
      if (current.includes(definition.fieldName)) return current;
      const next = [...current, definition.fieldName];
      persistFilterFieldNames(next);
      return next;
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={researchValidationSchema}
      validateOnMount={true}
      validateOnChange={true}
      onSubmit={handleFormikSubmit}>
      {({ handleSubmit, setFieldValue }) => {
        const handleRemoveField = (definition: ResearchFormFieldDefinition) => {
          void setFieldValue(definition.fieldName, getResearchFilterEmptyValue(definition.dataType), true);
          setActiveFieldNames((current) => {
            const next = current.filter((fieldName) => fieldName !== definition.fieldName);
            persistFilterFieldNames(next);
            return next;
          });
        };

        return (
          <Form
            id={formId}
            onSubmit={(event) => {
              // delay form submission slightly to account for input debouncing
              event.preventDefault();
              setTimeout(() => {
                handleSubmit();
              }, 300);
            }}
            className={styles['form']}>
            <div className={styles['title-bar']}>
              <span className={styles.title}>Advanced Leg Search</span>
              <IconButton
                title={isExpanded ? 'Collapse search filters' : 'Expand search filters'}
                aria-expanded={isExpanded}
                onClick={() => {
                  setIsExpanded((current) => !current);
                }}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </div>

            <Collapse in={isExpanded}>
              <Divider sx={{ margin: '12px 0 12px 0' }} />

              <div className={styles['form-content']}>
                <div className={styles['button-group']}>
                  <Autocomplete
                    options={availableFields}
                    getOptionLabel={(option) => option.label}
                    value={null}
                    inputValue={addFilterInputValue}
                    onInputChange={(_event, value, reason) => {
                      // only keep text the user typed; selecting an option ('selectOption'/'reset') clears the input
                      setAddFilterInputValue(reason === 'input' ? value : '');
                    }}
                    disabled={availableFields.length === 0}
                    onChange={(_event, value) => {
                      if (value) handleAddField(value);
                    }}
                    className={styles['add-filter']}
                    slotProps={{
                      popper: { style: { width: 'fit-content' } },
                      paper: { sx: { '& .MuiAutocomplete-option': { whiteSpace: 'nowrap' } } }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Add Filter'
                        placeholder='Select a field'
                      />
                    )}
                  />
                  <SubmitFormButton
                    formName={formId}
                    submitButtonText='Search'
                    startAdornment={<SearchIcon fontSize='small' />}
                    className={styles['submit-btn']}
                  />
                  <Button
                    type='reset'
                    title='Clear'
                    className={styles['clear-btn']}
                    variant='contained'
                    color='secondary'
                    onClick={() => {
                      setSearchParams([]);
                    }}
                    startIcon={<ClearIcon fontSize='small' />}>
                    Clear
                  </Button>
                </div>
                <Divider
                  orientation='vertical'
                  flexItem
                />
                <div className={styles['filter-list']}>
                  {activeFieldNames.map((fieldName) => {
                    const definition = RESEARCH_FORM_FIELD_DEFINITIONS.find((item) => item.fieldName === fieldName);
                    if (!definition) return null;

                    return (
                      <div
                        key={fieldName}
                        className={styles['filter-item']}>
                        {renderFilterField(definition)}
                        {!definition.pinned && (
                          <IconButton
                            title={`Remove ${definition.label} filter`}
                            onClick={() => {
                              handleRemoveField(definition);
                            }}>
                            <RemoveIcon fontSize='small' />
                          </IconButton>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Collapse>
          </Form>
        );
      }}
    </Formik>
  );
}
